import type { PlainMessage } from '@bufbuild/protobuf';

import {
  NoteDentition,
  NoteTeethGroup,
  type NoteTeethBlock,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

// One row of the chart, split at the midline: the patient's right half is
// drawn on the chart's left, each half listed as it is drawn, left to right.
export type TeethChartRow = { right: string[]; left: string[] };

// Each jaw's rows run from the chart's edge to the bite line, so the
// permanent teeth are outermost and the primary ones meet at the bite.
export type TeethChart = { upper: TeethChartRow[]; lower: TeethChartRow[] };

const PERMANENT_UPPER: TeethChartRow = {
  right: ['18', '17', '16', '15', '14', '13', '12', '11'],
  left: ['21', '22', '23', '24', '25', '26', '27', '28'],
};
const PERMANENT_LOWER: TeethChartRow = {
  right: ['48', '47', '46', '45', '44', '43', '42', '41'],
  left: ['31', '32', '33', '34', '35', '36', '37', '38'],
};
const PRIMARY_UPPER: TeethChartRow = {
  right: ['55', '54', '53', '52', '51'],
  left: ['61', '62', '63', '64', '65'],
};
const PRIMARY_LOWER: TeethChartRow = {
  right: ['85', '84', '83', '82', '81'],
  left: ['71', '72', '73', '74', '75'],
};

// The permanent FDI quadrants each group covers; a primary tooth's quadrant
// (5–8) counts as the permanent one four below it.
const GROUP_QUADRANTS: Record<NoteTeethGroup, number[]> = {
  [NoteTeethGroup.NoteTeethGroupInvalidValue]: [],
  [NoteTeethGroup.NoteTeethGroupUpper]: [1, 2],
  [NoteTeethGroup.NoteTeethGroupLower]: [3, 4],
  [NoteTeethGroup.NoteTeethGroupUpperRight]: [1],
  [NoteTeethGroup.NoteTeethGroupUpperLeft]: [2],
  [NoteTeethGroup.NoteTeethGroupLowerLeft]: [3],
  [NoteTeethGroup.NoteTeethGroupLowerRight]: [4],
  [NoteTeethGroup.NoteTeethGroupRight]: [1, 4],
  [NoteTeethGroup.NoteTeethGroupLeft]: [2, 3],
};

const PRIMARY_QUADRANT_OFFSET = 4;

export const getTeethChart = (dentition: NoteDentition): TeethChart => {
  switch (dentition) {
    case NoteDentition.NoteDentitionPrimary:
      return { upper: [PRIMARY_UPPER], lower: [PRIMARY_LOWER] };
    case NoteDentition.NoteDentitionMixed:
      return {
        upper: [PERMANENT_UPPER, PRIMARY_UPPER],
        lower: [PRIMARY_LOWER, PERMANENT_LOWER],
      };
    default:
      return { upper: [PERMANENT_UPPER], lower: [PERMANENT_LOWER] };
  }
};

// An empty set in the template offers every tooth of the dentition.
export const isNoteToothOffered = (
  block: PlainMessage<NoteTeethBlock>,
  tooth: string,
) => !block.Teeth.length || block.Teeth.includes(tooth);

export const isToothInNoteTeethGroup = (
  tooth: string,
  group: NoteTeethGroup,
) => {
  const quadrant = Number(tooth.charAt(0));
  return GROUP_QUADRANTS[group].includes(
    quadrant > PRIMARY_QUADRANT_OFFSET
      ? quadrant - PRIMARY_QUADRANT_OFFSET
      : quadrant,
  );
};

// The number a quadrant marker shows: FDI 1–4, or 5–8 on a primary chart.
export const getNoteTeethQuadrantNumber = (
  group: NoteTeethGroup,
  dentition: NoteDentition,
) => {
  const [quadrant] = GROUP_QUADRANTS[group];
  return dentition === NoteDentition.NoteDentitionPrimary
    ? quadrant + PRIMARY_QUADRANT_OFFSET
    : quadrant;
};
